import React, { useEffect, useMemo, useState } from 'react';

import { Button, Divider, IconButton, Typography } from '@material-ui/core';
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined';
import { useFormik } from 'formik';
import { get } from 'lodash';
import { useTranslation } from 'react-i18next';

import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';
import { ReactComponent as PenIcon } from 'assets/icons/pen.svg';
import DialogGrid from 'components/_dialogs/_components/DialogGrid';
import CenteredGrid from 'components/CenteredGrid/CenteredGrid';
import ColoredButton from 'components/ColoredButton';
import FormFileInput from 'components/FormFileInput';
import FormTextInput from 'components/FormTextInput';
import downloadFromUrl from 'helpers/downloadFromUrl/downloadFromUrl';
import general_messages from 'messages/general_messages';
import { Id } from 'types/Id';

import useStyles from './IncidentReportAttachments.styles';

export type StoredIncidentAttachment = {
  id: Id;
  name: string;
  url: string;
  description?: string;
};

export type NewIncidentAttachment = {
  file: File;
  name: string;
  description: string;
  localId: string;
};

export type IncidentAttachmentValue = StoredIncidentAttachment | NewIncidentAttachment;

type Props = {
  acceptedTypes?: string[];
  disabled?: boolean;
  formik: any;
  id: string;
  label: string;
  listOnly?: boolean;
  onAddAttachment: (file: File, description: string) => Promise<void>;
  onRemoveAttachment: (attachment: StoredIncidentAttachment) => Promise<void>;
  onUpdateAttachment: (attachment: StoredIncidentAttachment, description: string) => Promise<void>;
  validateFile?: (file: File) => string | null | undefined;
};

type DialogState = {
  index: number | null;
  open: boolean;
};

type DialogForm = {
  file: File | '';
  description: string;
};

export const isStoredIncidentAttachment = (attachment: IncidentAttachmentValue): attachment is StoredIncidentAttachment =>
  Boolean((attachment as StoredIncidentAttachment).id);

export const isNewIncidentAttachment = (attachment: IncidentAttachmentValue): attachment is NewIncidentAttachment =>
  Boolean((attachment as NewIncidentAttachment).file);

const IncidentReportAttachments: React.FC<Props> = ({
  acceptedTypes,
  disabled,
  formik,
  id,
  label,
  listOnly,
  onAddAttachment,
  onRemoveAttachment,
  onUpdateAttachment,
  validateFile,
}) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const value = get(formik.values, id, []) as IncidentAttachmentValue[];
  const error = get(formik.errors, id);
  const touched = get(formik.touched, id);
  const [dialogState, setDialogState] = useState<DialogState>({ index: null, open: false });
  const [sending, setSending] = useState(false);
  const attachmentToEdit = dialogState.index === null ? null : value[dialogState.index];
  const isEdit = Boolean(attachmentToEdit);

  const setAttachments = (nextValue: IncidentAttachmentValue[]) => {
    formik.setFieldValue(id, nextValue);
  };

  const closeDialog = () => setDialogState({ index: null, open: false });
  const openAddDialog = () => setDialogState({ index: null, open: true });
  const openEditDialog = (index: number) => () => setDialogState({ index, open: true });

  const onRemove = (index: number) => async () => {
    const attachment = value[index];
    if (isStoredIncidentAttachment(attachment)) {
      setSending(true);
      await onRemoveAttachment(attachment);
      setSending(false);
      return;
    }

    const nextValue = [...value];
    nextValue.splice(index, 1);
    setAttachments(nextValue);
  };

  const dialogFormik = useFormik<DialogForm>({
    enableReinitialize: true,
    initialValues: {
      file: '',
      description: attachmentToEdit?.description || '',
    },
    onSubmit: async formValues => {
      setSending(true);
      if (isEdit && attachmentToEdit && isStoredIncidentAttachment(attachmentToEdit)) {
        await onUpdateAttachment(attachmentToEdit, formValues.description);
      } else if (formValues.file) {
        await onAddAttachment(formValues.file, formValues.description);
      }
      formik.setFieldError?.(id, undefined);
      setSending(false);
      closeDialog();
    },
  });

  useEffect(() => {
    if (dialogState.open) {
      dialogFormik.setValues({
        file: '',
        description: attachmentToEdit?.description || '',
      });
    }
  }, [dialogState.open, dialogState.index]);

  const dialogIsValid = useMemo(() => {
    if (isEdit) return true;
    return Boolean(dialogFormik.values.file);
  }, [dialogFormik.values.file, isEdit]);

  const validateDialogFile = (file: File) => {
    const invalidMessage = validateFile?.(file);
    if (invalidMessage) {
      formik.setFieldTouched?.(id, true, false);
      formik.setFieldError?.(id, invalidMessage);
    }
    return invalidMessage;
  };

  const dialogTitle = isEdit ? t(general_messages.edit) : label;
  const dialogActions = (
    <CenteredGrid gridGap={2} withoutPadding>
      {/* @ts-ignore */}
      <ColoredButton
        customColor='secondary'
        disabled={!dialogIsValid || sending}
        form={`${id}-attachment-dialog-form`}
        type='submit'
        variant='outlined'
      >
        {t(isEdit ? general_messages.save : general_messages.add)}
      </ColoredButton>
      <Button onClick={closeDialog} size='small'>
        {t(general_messages.cancel)}
      </Button>
    </CenteredGrid>
  );

  const renderDescription = (description?: string) => {
    if (description) {
      return <Typography className={styles.documentDescription}>{description}</Typography>;
    }

    return <Typography className={styles.documentDescription}>{t(general_messages.no_data[0], general_messages.no_data[1])}</Typography>;
  };

  const getActionsGridColumns = () => {
    if (listOnly) return '32px';
    return 'repeat(3, 32px)';
  };

  const actionsStyle = {
    gridTemplateColumns: getActionsGridColumns(),
  };

  const renderDialog = () => (
    // @ts-ignore
    <DialogGrid dialogActions={dialogActions} onClose={closeDialog} open={dialogState.open} sending={sending} title={dialogTitle}>
      <form id={`${id}-attachment-dialog-form`} onSubmit={dialogFormik.handleSubmit}>
        <CenteredGrid gridGap={2} withoutPadding>
          {!isEdit && (
            <FormFileInput
              acceptedTypes={acceptedTypes}
              formik={dialogFormik}
              id='file'
              label={t(general_messages.upload_file)}
              required
              validateFile={validateDialogFile}
            />
          )}
          <FormTextInput formik={dialogFormik} id='description' label={t(general_messages.description)} multiline rows={3} />
        </CenteredGrid>
      </form>
    </DialogGrid>
  );

  const onDownload = (attachment: StoredIncidentAttachment) => async (event: React.MouseEvent) => {
    event.preventDefault();
    await downloadFromUrl(attachment.url, attachment.name);
  };

  return (
    <div className={styles.root}>
      {!listOnly && (
        // @ts-ignore
        <ColoredButton className={styles.button} customColor='secondary' disabled={disabled} onClick={openAddDialog} variant='outlined'>
          {label}
        </ColoredButton>
      )}
      <div className={styles.filesSection}>
        {value.length ? (
          value.map((attachment, index) => (
            <div key={isStoredIncidentAttachment(attachment) ? attachment.id : attachment.localId}>
              <div className={styles.documentRow}>
                <div className={styles.documentInfo}>
                  <Typography className={styles.documentName}>{attachment.name}</Typography>
                  {renderDescription(attachment.description)}
                </div>
                <div className={styles.documentActions} style={actionsStyle}>
                  {isStoredIncidentAttachment(attachment) && (
                    <IconButton onClick={onDownload(attachment)} size='small'>
                      <DownloadIcon />
                    </IconButton>
                  )}
                  {!listOnly && (
                    <>
                      <IconButton disabled={disabled || sending} onClick={openEditDialog(index)} size='small'>
                        <PenIcon />
                      </IconButton>
                      <IconButton disabled={disabled || sending} onClick={onRemove(index)} size='small'>
                        <DeleteOutlinedIcon color='secondary' fontSize='small' />
                      </IconButton>
                    </>
                  )}
                </div>
              </div>
              <Divider />
            </div>
          ))
        ) : (
          <Typography className={styles.emptyState}>{t(general_messages.no_data[0], general_messages.no_data[1])}</Typography>
        )}
      </div>
      {touched && error && (
        <Typography className={styles.error} color='error' variant='caption'>
          {error}
        </Typography>
      )}
      {dialogState.open && renderDialog()}
    </div>
  );
};

export default IncidentReportAttachments;
